import { Controller } from "@hotwired/stimulus";

// The row of tabs under a detail page's title: in-page links that stick under
// the header while the page scrolls. It marks the tab of the section being
// read (aria-current, and data-active for the look) and slides that tab into
// view inside the row, which scrolls sideways on a phone.
//
//   <nav data-controller="section-nav" data-turbo="false">
//     <a href="#harita" data-section-nav-target="link">Harita</a>
//   </nav>
//
// The links work without it; this only says where the reader is. They are kept
// out of Turbo, which would treat each as a visit and re-render the page: the
// fresh header starts at its full height and shrinks back, and the mark taken
// while it does lands on the section before.
export default class extends Controller {
  static targets = ["link"];

  connect() {
    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });
    // The page can move under a still window too — a photo loading above, the
    // header settling to its compact height — and no scroll event says so.
    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(document.body);
    this.update();
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll);
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.frame);
  }

  update() {
    // The section being read is the last one whose top has passed the foot of
    // the row; above the first one, no tab is.
    const line = this.element.getBoundingClientRect().bottom + 24;
    let current = null;
    for (const link of this.linkTargets) {
      // A tab the width hides (the rail's, from lg) never takes the mark.
      if (!link.offsetParent) continue;
      const section = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (section && section.getBoundingClientRect().top <= line) current = link;
    }

    this.linkTargets.forEach((link) => {
      const active = link === current;
      link.toggleAttribute("data-active", active);
      if (active) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    });

    // Scrolls the row, never the page: scrollIntoView would move the window too.
    if (current && current !== this.current) {
      this.element.scrollTo({ left: current.offsetLeft - 16, behavior: "smooth" });
    }
    this.current = current;
  }
}
